import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import { TypedMemo } from '../../../../../common/types';
import { LoadingView } from '../../../../../common/views/LoadingView';
import type { CoverRedoDialogModel } from './useCoverRedoDialog';
import type { CoverRedoError } from './coverRedoErrors';
import { CoverRedoPair } from './CoverRedoPair';
import { CoverRedoGroups } from './CoverRedoGroups';
import { CoverRedoPosts } from './CoverRedoPosts';
import { CoverRedoFooter } from './CoverRedoFooter';
import { coverRedoMessages as messages } from './coverRedoMessages';
import { withBrandWord } from '../../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover redo; each part uses its own slice of it */
import styles from './CoverRedo.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ERROR_LINE = {
  busy: messages.errorBusy,
  noMaster: messages.errorNoMaster,
  masterProcessing: messages.errorMasterProcessing,
  other: messages.errorOther,
} as const;

/** The error line over the footer (b3). It stays until a tick changes or the user tries again. */
const ErrorLine: React.FC<{ error: CoverRedoError | undefined }> = ({ error }) => {
  const intl = useIntl();
  if (error === undefined || error.kind === 'videoCredits') {
    return null;
  }
  const text =
    error.kind === 'costChanged'
      ? intl.formatMessage(messages.errorCostChanged, { credits: error.credits })
      : intl.formatMessage(ERROR_LINE[error.kind]);
  return (
    <p className={styles.error} role="alert" data-testid="cover-redo-error">
      {withBrandWord(text)}
    </p>
  );
};

/**
 * "Redo with the new cover" (design kit-redo-cover b): the groups to make again with their ticks
 * and credits, the files that are left out, the posts that move, the cost against the credits left
 * and the confirm. A bottom sheet below 576 px. Nothing is charged before the confirm.
 */
const CoverRedoDialogComponent: React.FC<{ model: CoverRedoDialogModel }> = ({ model }) => {
  const intl = useIntl();
  const titleId = useId();
  const { state } = model;
  return (
    <Modal
      isOpen={model.isOpen}
      toggle={model.close}
      centered
      labelledBy={titleId}
      className={styles.dialog}
      contentClassName={styles.content}
      returnFocusAfterClose
    >
      <span className={styles.grab} aria-hidden="true" />
      <div className={styles.head} data-testid="cover-redo-dialog">
        <div className={styles.headText}>
          <h3 id={titleId}>{intl.formatMessage(messages.dialogTitle)}</h3>
          <p>{intl.formatMessage(messages.dialogLead)}</p>
        </div>
        {state !== undefined && (
          <CoverRedoPair previousUrl={state.cover.previousUrl} currentUrl={state.cover.currentUrl} small />
        )}
      </div>
      {state === undefined ? (
        <div className={styles.loading}>{model.error === undefined ? <LoadingView /> : null}</div>
      ) : (
        <>
          <CoverRedoGroups model={model} />
          <CoverRedoPosts model={model} />
        </>
      )}
      <ErrorLine error={model.error} />
      <CoverRedoFooter model={model} />
    </Modal>
  );
};

export const CoverRedoDialog = TypedMemo(CoverRedoDialogComponent);
